import {
  ExclamationCircleOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoreOutlined,
} from '@ant-design/icons'
import { Dropdown, Form, Layout, Modal, Space, theme, Typography } from 'antd'
import {
  useContext,
  useEffect,
  useState,
  forwardRef,
  useRef,
  useMemo,
} from 'react'
import { Outlet, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Button from '../common/Button'
import { useVehicles } from '../../context/vehicle_context'
import { deleteRequest, getRequest } from '../../services/base'
import Sidebar from './Sidebar'
import SelectInput from '../common/SelectInput'
import { NavContext } from '../../context/global_context'
import NoDataFound from '../../components/common/NoDataFound'
import NotFound from '../../pages/404'
import dayjs from 'dayjs'
import { formatDateTime } from '../../utils/common'

const { Header, Content } = Layout
const { Title, Paragraph } = Typography

function VehicleWorkspaceLayout() {
  const { vehicle: vehicleId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const {
    vehicles,
    setVehiclesData,
    selectedVehicle,
    setSelectedVehicle,
    hasBlockers,
    setHasBlockers,
  } = useVehicles()

  const [collapsed, setCollapsed] = useState(false)
  const [error, setError] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [state, dispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loaded) {
      dispatch({
        type: 'loader',
        data: {
          caption: 'Fetching Vehicle Details',
          showLoader: true,
        },
        notification: {},
      })
      return
    }
    dispatch({
      type: 'loader',
      data: {
        caption: '',
        showLoader: false,
      },
      notification: {},
    })
  }, [loaded])

  useEffect(() => {
    const getVehicle = async () => {
      let res = await getRequest(`/api/vehicles/vehicle_details/${vehicleId}/`)
      if (res.msg && res.status == false) {
        setSelectedVehicle(null)
        setLoaded(true)
        return
      }

      setSelectedVehicle(res)
      setLoaded(true)
    }

    if (vehicleId) getVehicle()

    return () => {
      setSelectedVehicle(null)
    }
  }, [vehicleId])

  const isRouteActive = (route) => {
    const parts = location.pathname.split('/').filter(Boolean)
    return parts[parts.length - 1] === route
  }

  /* Vehicle Deletion */
  const moreMenuItems = [
    {
      key: 'delete',
      label: 'Delete Vehicle',
      disabled: hasBlockers,
      className: 'danger'
    },
  ]
  const confirmDeleteVehicle = (vehicleId) => {
    modal.confirm({
      title: 'Confirm',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${vehicleId}"?`,
      onOk: async () => {
        try {
          await deleteVehicle(vehicleId)
        } catch (err) {
          console.log(err)
        }
      },
      centered: true,
      maskClosable: true,
      width: '40%',
    })
  }
  const deleteVehicle = async (vehicle_make_id) => {
    dispatch({
      type: 'loader',
      data: {
        caption: 'Deleting Vehicle .....',
        showLoader: true,
      },
      notification: {},
    })

    let res = await deleteRequest(
      `/api/vehicles/delete_vehicle/${vehicle_make_id}/`
    )
    if (res?.status) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: `Successfully Deleted ${vehicle_make_id}`,
        },
      })
      navigate(`/vehicles`)
    } else {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: `Something went wrong ${res?.msg}`,
        },
      })
    }
  }

  /* Change Selected Vehicle */
  /* const [selectVehicleOptions, setSelectVehicleOptions] = useState([])
  useEffect(() => {
    if(!selectedVehicle) return
    
    const getAllVehicles = async() => {
      let res = await getRequest(`/api/vehicles/`)
      if(res.msg && res.status == false) {
        setVehiclesData([])
        return
      }
      console.log(res)
      setVehiclesData(res.results)
    }
    getAllVehicles()
    
    return () => {
      setSelectVehicleOptions([])
    }
  }, [selectedVehicle])
  useEffect(() => {
    if(!vehicles || vehicles.length === 0) return

    const ddOptions = vehicles.map((v) => ({
      label: v.name.toUpperCase(),
      value: v.vehicle_id,
    }))
    setSelectVehicleOptions(ddOptions)
    setLoaded(true)

    return () => {
      setVehiclesData([])
    }
  }, [vehicles])
  const addVehicleSelection = () => {
    return (
      <Form
        id="vehicle_selection"
        name="vehicle_selection"
        initialValues={{ select_vehicle: selectedVehicle?.vehicle_id }}
      >
        <SelectInput
          name="select_vehicle"
          label="Select Vehicle"
          value={selectedVehicle?.vehicle_id}
          options={selectVehicleOptions}
          wrapperClass="select-vehicle-list"
          hideLabel={true}
          isRequired={false}
          showSearch={false}
          allowClear={false}
          onChange={(option) => {
            const { value, label } = option
            const v = vehicles.find((v) => v.vehicle_id === value)
            setSelectedVehicle(v)
            navigate(`/vehicles/${value}/importers`)
          }}
        />
      </Form>
    )
  } */

  return (
    <>
      <Layout className="vehicle-workspace-root">
        {/* Vehicle Top Bar */}
        {loaded && selectedVehicle && !searchParams.get('hide-nav') && (
          <Header className="subheader-container">
            <Space size={'middle'}>
              <Button
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
              />
              {/* {(loaded && vehicles) && addVehicleSelection()} */}
              <div className="vehicle-info">
                <Title level={3} className="vehicle-make">
                  {selectedVehicle?.make_description}
                </Title>

                <Title level={4} className="vehicle-model">
                  {selectedVehicle?.model_description}
                </Title>
              </div>
              <div className="vehicle-details">
                <Paragraph className="vehicle-make-model-id">
                  Make Model ID: {selectedVehicle?.make_model_id}{' '}
                </Paragraph>
                <Paragraph className="vehicle-update-date">
                  Last import:{' '}
                  {dayjs(selectedVehicle?.updated_at).format(
                    'DD-MM-YYYY hh:mm A'
                  )}
                </Paragraph>
              </div>
            </Space>

            <Space size={'middle'}>
              <Button
                onClick={(e) => {
                  if(isRouteActive('edit_vehicle')) return
                  navigate(`/vehicles/${vehicleId}/edit_vehicle`)
                }}
                disabled={hasBlockers}
                className={isRouteActive('edit_vehicle') ? 'selected' : ''}
              >
                Edit
              </Button>
              <Button
                onClick={(e) => {
                  if(isRouteActive('demo_payload')) return
                  navigate(`/vehicles/${vehicleId}/demo_payload`)
                }}
                disabled={hasBlockers}
                className={isRouteActive('demo_payload') ? 'selected' : ''}
              >
                Demo Payload
              </Button>
              <Button
                onClick={(e) => {
                  if(isRouteActive('csv_dependencies')) return
                  navigate(`/vehicles/${vehicleId}/csv_dependencies`)
                }}
                disabled={hasBlockers}
                className={isRouteActive('csv_dependencies') ? 'selected' : ''}
              >
                CSV Dependencies
              </Button>
              <Dropdown
                trigger={['click']}
                placement="bottomRight"
                disabled={hasBlockers}
                overlayStyle={{
                  marginTop: 12,
                }}
                menu={{
                  items: moreMenuItems,
                  onClick: ({ key }) => {
                    confirmDeleteVehicle(vehicleId)
                  },
                }}
              >
                <Button shape="circle" icon={<MoreOutlined />} />
              </Dropdown>
            </Space>
          </Header>
        )}

        {/* Right side */}
        <Layout className="vehicle-workspace">
          {/* Sidebar */}
          {loaded && selectedVehicle && !searchParams.get('hide-nav') && (
            <Sidebar
              vehicleId={vehicleId}
              collapsed={collapsed}
              hasBlockers={hasBlockers}
              className={`sidebar ${collapsed ? 'sider-collapsed' : 'sider-expanded'}`}
            />
          )}
          {/* Inner Routed RHS Content */}
          <Content className="content">
            {error ? (
              error
            ) : loaded && !selectedVehicle ? (
              <NotFound />
            ) : (
              loaded && selectedVehicle && <Outlet />
            )}
          </Content>
        </Layout>
      </Layout>
      {contextHolder}
    </>
  )
}

export default VehicleWorkspaceLayout
